import { Close as CloseIcon } from '@mui/icons-material';
import { Box, IconButton, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import type BackendService from '../../../services/admin/BackendService';
import type { SidebarTab } from './editorSidebarContext';
import PaletteTabBody from './PaletteTabBody';

interface PalettePanelProps {
  isNarrow: boolean;
  open: boolean;
  tab: SidebarTab;
  fundOpportunityId: string;
  backendService: BackendService;
  /** The content model type, e.g. 'FundOpportunity'. Needed for document region crops. */
  modelType?: string;
  /** The content model external ID. Needed for document region crops. */
  modelId?: string;
  onTabChange: (tab: SidebarTab) => void;
  onClose: () => void;
}

/** The Media tab shows its own (context-dependent) hint, so only Content has one here. */
const TAB_HINT: Record<SidebarTab, string | null> = {
  blocks: 'Drag and drop the content block you want to add',
  media: null,
  documents: null,
};

/** The drawer contents: tabs (Content / Media / Documents), a hint, and the active tab's scrollable body. */
const PalettePanel = React.forwardRef<HTMLDivElement, PalettePanelProps>(
  (
    {
      isNarrow,
      open,
      tab,
      fundOpportunityId,
      backendService,
      modelType = 'FundOpportunity',
      modelId = '',
      onTabChange,
      onClose,
    },
    ref,
  ) => (
    <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: 1,
          pr: 2,
          pt: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_event, value: SidebarTab) => onTabChange(value)}
          aria-label="Editor sidebar"
          sx={{
            minHeight: 0,
            '& .MuiTab-root': {
              minHeight: 0,
              minWidth: 0,
              px: 2,
              py: 1,
              fontWeight: 600,
              color: Palette.default.disabledText,
            },
            '& .Mui-selected': { color: `${Palette.default.buttonBg} !important` },
            '& .MuiTabs-indicator': { backgroundColor: Palette.default.buttonBg },
          }}
        >
          <Tab value="blocks" label="Content" />
          <Tab value="media" label="Media" />
          <Tab value="documents" label="Docs" />
        </Tabs>
        <IconButton size="small" aria-label="Close sidebar" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {TAB_HINT[tab] && (
        // Matches PaletteTabBody's own p:1.5 wrapper, so this sits exactly where the
        // Media tab's own hint text does (inside that padding) rather than drifting.
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', px: 1.5, pt: 1.5 }}
        >
          {TAB_HINT[tab]}
        </Typography>
      )}

      <PaletteTabBody
        open={open}
        tab={tab}
        isNarrow={isNarrow}
        fundOpportunityId={fundOpportunityId}
        backendService={backendService}
        modelType={modelType}
        modelId={modelId}
      />
    </Box>
  ),
);
PalettePanel.displayName = 'PalettePanel';

export default PalettePanel;
